import { useEffect, useId, useRef } from "react";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLAttributes,
  ReactNode,
} from "react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "compact" | "default" | "large";

export interface UiButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingLabel?: string;
  leadingIcon?: ReactNode;
}

export function buttonClassName({
  variant = "secondary",
  size = "default",
  className,
}: Pick<UiButtonProps, "variant" | "size" | "className">): string {
  return [
    "button",
    `button-${variant}`,
    `button-${size}`,
    className,
  ].filter(Boolean).join(" ");
}

export function UiButton({
  variant = "secondary",
  size = "default",
  loading = false,
  loadingLabel = "������",
  leadingIcon,
  className,
  children,
  disabled,
  type = "button",
  ...props
}: UiButtonProps) {
  return (
    <button
      {...props}
      className={buttonClassName({ variant, size, className })}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading ? <span className="button-spinner" aria-hidden="true" /> : leadingIcon}
      <span>{loading ? loadingLabel : children}</span>
    </button>
  );
}

export interface UiLinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export function UiLinkButton({
  variant = "secondary",
  size = "default",
  leadingIcon,
  trailingIcon,
  className,
  children,
  ...props
}: UiLinkButtonProps) {
  return (
    <a {...props} className={buttonClassName({ variant, size, className })}>
      {leadingIcon}
      <span>{children}</span>
      {trailingIcon}
    </a>
  );
}

export interface SurfaceCardProps extends HTMLAttributes<HTMLElement> {
  as?: "article" | "section" | "div";
  tone?: "default" | "soft" | "brand" | "dark";
  interactive?: boolean;
}

export function SurfaceCard({
  as: Component = "article",
  tone = "default",
  interactive = false,
  className,
  ...props
}: SurfaceCardProps) {
  return (
    <Component
      {...props}
      className={[
        "surface-card",
        `surface-card--${tone}`,
        interactive ? "surface-card--interactive" : undefined,
        className,
      ].filter(Boolean).join(" ")}
    />
  );
}

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: "neutral" | "brand" | "success" | "warning" | "danger";
  dot?: boolean;
}

export function StatusBadge({
  tone = "neutral",
  dot = false,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      {...props}
      className={["status-badge", `status-badge--${tone}`, className]
        .filter(Boolean)
        .join(" ")}
    >
      {dot ? <i aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export interface InlineFeedbackProps extends HTMLAttributes<HTMLDivElement> {
  tone?: "info" | "success" | "warning" | "danger";
  title: string;
}

export function InlineFeedback({
  tone = "info",
  title,
  children,
  className,
  ...props
}: InlineFeedbackProps) {
  return (
    <div
      {...props}
      className={["inline-feedback", `inline-feedback--${tone}`, className]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="inline-feedback-icon" aria-hidden="true">
        {tone === "success" ? "?" : tone === "danger" ? "!" : tone === "warning" ? "!" : "i"}
      </span>
      <div>
        <strong>{title}</strong>
        {children ? <p>{children}</p> : null}
      </div>
    </div>
  );
}

export interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  width?: string;
}

export function Skeleton({ width = "100%", className, style, ...props }: SkeletonProps) {
  return (
    <span
      {...props}
      aria-hidden="true"
      className={["skeleton", className].filter(Boolean).join(" ")}
      style={{ ...style, width }}
    />
  );
}

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  action?: ReactNode;
  visual?: ReactNode;
}

export function EmptyState({
  title,
  description,
  action,
  visual,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      {...props}
      className={["empty-state", className].filter(Boolean).join(" ")}
    >
      <div className="empty-state-visual" aria-hidden="true">
        {visual ?? <><i /><i /><i /></>}
      </div>
      <strong>{title}</strong>
      <p>{description}</p>
      {action ? <div className="empty-state-action">{action}</div> : null}
    </div>
  );
}
export interface ModalDialogProps {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}

export function ModalDialog({
  open,
  title,
  description,
  children,
  footer,
  onClose,
}: ModalDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="modal-dialog"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal-surface">
        <header>
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <button className="icon-button" type="button" aria-label="关闭弹窗" onClick={onClose}>×</button>
        </header>
        <div className="modal-content">{children}</div>
        {footer ? <footer>{footer}</footer> : null}
      </div>
    </dialog>
  );
}
